import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Search, X, Phone, Car, Mail, MapPin } from 'lucide-react';
import { customerAPI } from './services/api';
import { CustomerDetailModal } from './components/Modals/CustomerDetailModal';

const digits = value => String(value ?? '').replace(/\D/g, '');
const text = value => String(value ?? '').trim();
const customerName = c => c?.name || [c?.firstName,c?.lastName].filter(Boolean).join(' ') || 'UNKNOWN CUSTOMER';
const vehicleName = c => c?.vehicle || [c?.vehicleYear,c?.vehicleMake,c?.vehicleModel,c?.vehicleTrim].filter(Boolean).join(' ') || 'VEHICLE NOT SPECIFIED';
const phone = c => c?.phone || c?.cellPhone || c?.cPhone || '';
const address = c => [c?.address,c?.city,c?.state,c?.zipCode||c?.zip].filter(Boolean).join(', ');

const haystack = c => [
  customerName(c), phone(c), c?.email, c?.address, c?.city, c?.state, c?.zipCode, c?.zip,
  c?.vin, c?.vehicle, c?.vehicleYear, c?.vehicleMake, c?.vehicleModel, c?.vehicleTrim,
  c?.stockNumber, c?.source, c?.assignedTo, c?.assignedAppraiser
].filter(Boolean).join(' ').toLowerCase();

const scoreCustomer = (c, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const name = customerName(c).toLowerCase();
  const vin = text(c?.vin).toLowerCase();
  const p = digits(phone(c));
  const qDigits = digits(q);
  const all = haystack(c);
  let score = 0;
  if (name === q) score += 100;
  else if (name.startsWith(q)) score += 75;
  else if (name.includes(q)) score += 55;
  if (vin === q) score += 100;
  else if (vin.includes(q)) score += 60;
  if (qDigits.length >= 3 && p.includes(qDigits)) score += p === qDigits ? 100 : 70;
  const words = q.split(/\s+/).filter(Boolean);
  if (words.every(word => all.includes(word))) score += 35 + words.length * 3;
  return score;
};

function QuickCustomerSearch() {
  const [open,setOpen]=useState(false);
  const [query,setQuery]=useState('');
  const [customers,setCustomers]=useState([]);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [detailCustomer,setDetailCustomer]=useState(null);
  const [toast,setToast]=useState('');
  const inputRef=useRef(null);

  const authenticated = Boolean(localStorage.getItem('token') || localStorage.getItem('vincentrix_token'));

  const loadCustomers = async () => {
    setLoading(true); setError('');
    try {
      const data=await customerAPI.getAll();
      setCustomers(Array.isArray(data?.customers)?data.customers:[]);
    } catch (e) {
      setError(e.message || 'Unable to load customers');
    } finally { setLoading(false); }
  };

  const showToast=(message)=>{
    setToast(message||'');
    window.clearTimeout(showToast.timer);
    showToast.timer=window.setTimeout(()=>setToast(''),2600);
  };

  useEffect(()=>{
    const onKey=e=>{
      const target=e.target;
      const typing=target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;
      if ((e.metaKey||e.ctrlKey) && e.key.toLowerCase()==='k') { e.preventDefault(); setOpen(true); }
      else if (e.key==='/' && !typing) { e.preventDefault(); setOpen(true); }
      else if (e.key==='Escape') setOpen(false);
    };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[]);

  useEffect(()=>{
    if(!open) return;
    loadCustomers();
    const t=setTimeout(()=>inputRef.current?.focus(),30);
    return()=>clearTimeout(t);
  },[open]);

  const results=useMemo(()=>{
    if(!query.trim()) return customers.slice(0,8);
    return customers.map(c=>({c,score:scoreCustomer(c,query)})).filter(x=>x.score>0).sort((a,b)=>b.score-a.score).slice(0,12).map(x=>x.c);
  },[customers,query]);

  if(!authenticated) return null;

  return <>
    <style>{`
      .vc-qfind-launch{position:fixed;right:22px;top:82px;z-index:1400;border:1px solid rgba(96,165,250,.28);background:rgba(7,16,31,.92);backdrop-filter:blur(12px);color:#dbeafe;border-radius:12px;padding:10px 13px;font-size:11px;font-weight:900;display:flex;align-items:center;gap:8px;cursor:pointer;box-shadow:0 12px 30px rgba(0,0,0,.3)}
      .vc-qfind-launch kbd{font:inherit;font-size:9px;color:rgba(255,255,255,.38);border:1px solid rgba(255,255,255,.11);border-radius:6px;padding:2px 5px}
      .vc-qfind-overlay{position:fixed;inset:0;z-index:3200;background:rgba(0,0,0,.72);backdrop-filter:blur(9px);display:flex;align-items:flex-start;justify-content:center;padding:10vh 16px 24px;box-sizing:border-box}
      .vc-qfind-panel{width:min(720px,96vw);max-height:78vh;background:linear-gradient(180deg,#081321,#050b15);border:1px solid rgba(96,165,250,.25);border-radius:18px;box-shadow:0 30px 90px rgba(0,0,0,.65);overflow:hidden;color:#fff;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif}
      .vc-qfind-head{display:flex;align-items:center;gap:10px;padding:14px 16px;border-bottom:1px solid rgba(255,255,255,.08)}
      .vc-qfind-input{flex:1;border:0;outline:0;background:transparent;color:#fff;font-size:17px;font-weight:700;text-transform:none!important}.vc-qfind-input::placeholder{color:rgba(255,255,255,.28)}
      .vc-qfind-close{border:0;background:transparent;color:rgba(255,255,255,.55);cursor:pointer;padding:5px}
      .vc-qfind-hint{padding:8px 16px;font-size:9px;color:rgba(255,255,255,.35);border-bottom:1px solid rgba(255,255,255,.05)}
      .vc-qfind-results{max-height:58vh;overflow:auto;padding:8px}.vc-qfind-result{width:100%;text-align:left;border:1px solid transparent;background:transparent;color:#fff;border-radius:12px;padding:12px;cursor:pointer;display:grid;grid-template-columns:1fr auto;gap:12px}.vc-qfind-result:hover,.vc-qfind-result:focus{outline:none;background:rgba(59,130,246,.09);border-color:rgba(96,165,250,.18)}
      .vc-qfind-name{font-size:13px;font-weight:900}.vc-qfind-vehicle{font-size:11px;color:#bfdbfe;margin-top:4px}.vc-qfind-meta{display:flex;gap:10px;flex-wrap:wrap;margin-top:6px;color:rgba(255,255,255,.42);font-size:9px}.vc-qfind-meta span{display:flex;align-items:center;gap:4px}.vc-qfind-vin{font-size:9px;color:rgba(255,255,255,.3);letter-spacing:.5px;text-align:right}.vc-qfind-status{font-size:9px;margin-top:5px;text-align:right;color:#86efac;text-transform:uppercase;font-weight:900}.vc-qfind-empty{padding:34px 18px;text-align:center;color:rgba(255,255,255,.38);font-size:11px}.vc-qfind-toast{position:fixed;left:50%;bottom:25px;transform:translateX(-50%);z-index:4000;background:#081321;border:1px solid rgba(96,165,250,.25);color:#fff;border-radius:10px;padding:10px 14px;font-size:11px;box-shadow:0 14px 40px rgba(0,0,0,.45)}
      @media(max-width:700px){.vc-qfind-launch{top:auto;right:auto;left:14px;bottom:16px;padding:11px 13px}.vc-qfind-launch kbd{display:none}.vc-qfind-overlay{padding:7vh 8px 12px}.vc-qfind-panel{max-height:86vh}.vc-qfind-result{grid-template-columns:1fr}.vc-qfind-vin,.vc-qfind-status{text-align:left}}
    `}</style>
    <button className="vc-qfind-launch" onClick={()=>setOpen(true)}><Search size={14}/> Find Customer <kbd>⌘K</kbd></button>
    {open&&<div className="vc-qfind-overlay" onMouseDown={e=>{if(e.target===e.currentTarget)setOpen(false)}}>
      <div className="vc-qfind-panel">
        <div className="vc-qfind-head"><Search size={19} color="#60a5fa"/><input ref={inputRef} className="vc-qfind-input" type="search" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search name, phone, VIN, vehicle, email, address..."/><button className="vc-qfind-close" onClick={()=>setOpen(false)}><X size={19}/></button></div>
        <div className="vc-qfind-hint">Search any part of a customer record. Phone numbers work with or without dashes.</div>
        <div className="vc-qfind-results">
          {loading?<div className="vc-qfind-empty">Loading customers…</div>:error?<div className="vc-qfind-empty">{error}</div>:results.length?results.map(c=><button key={c.id||c._id||c.vin||customerName(c)} className="vc-qfind-result" onClick={()=>{setOpen(false);setDetailCustomer(c)}}>
            <div><div className="vc-qfind-name">{customerName(c)}</div><div className="vc-qfind-vehicle">{vehicleName(c)}</div><div className="vc-qfind-meta">{phone(c)&&<span><Phone size={10}/>{phone(c)}</span>}{c.email&&<span><Mail size={10}/>{c.email}</span>}{address(c)&&<span><MapPin size={10}/>{address(c)}</span>}</div></div>
            <div><div className="vc-qfind-vin">{c.vin||''}</div><div className="vc-qfind-status">{String(c.acquisitionStatus||c.status||'').replace(/-/g,' ')}</div></div>
          </button>):<div className="vc-qfind-empty">No customer matched “{query}”.</div>}
        </div>
      </div>
    </div>}
    <CustomerDetailModal show={Boolean(detailCustomer)} customer={detailCustomer} onClose={()=>setDetailCustomer(null)} showToast={showToast}/>
    {toast&&<div className="vc-qfind-toast">{toast}</div>}
  </>;
}

const mount=()=>{
  if(document.getElementById('vc-quick-customer-search-root')) return;
  const host=document.createElement('div');
  host.id='vc-quick-customer-search-root';
  document.body.appendChild(host);
  createRoot(host).render(<QuickCustomerSearch/>);
};

if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',mount,{once:true}); else mount();

export {};
